<template>
  <AppLink
    class="guide-quotes-quote"
    :to="guideProfilePath"
    target="_blank"
    @click="handleGuideClick"
  >
    <div class="guide-quotes-quote__title">
      <ArticleContentRender :content="title" />
    </div>
    <div class="guide-quotes-quote__text">
      <ArticleContentRender :content="text" />
    </div>

    <div class="guide-quotes-quote__info">
      <img
        v-if="avatarLink"
        class="guide-quotes-quote__avatar"
        :src="avatarLink"
        loading="lazy"
      />
      <div class="guide-quotes-quote__info-text">
        <span>
          <span class="guide-quotes-quote__guide-name">{{ name }}</span>
          <span>, {{ fullLocation }}</span>
        </span>

        <div
          v-if="experienceCount"
          class="guide-quotes-quote__experiences"
        >
          {{ formattedExperienceCount }}
        </div>
      </div>
    </div>
  </AppLink>
</template>

<script lang="ts" setup>
  import { computed } from 'vue';

  import { ArticleContentRender } from '@/features/articleContent';
  import type { ArticleContent } from '@/features/articleContent';
  import { pluralize } from '@/shared/utils/language';

  export interface Props {
    id: number;
    name: string;
    avatarLink: string;
    location: string;
    experienceCount: number;
    title: ArticleContent;
    text: ArticleContent;
  }

  const props = defineProps<Props>();

  const emit = defineEmits<{
    (e: 'guide-clicked', guideId: number): void;
  }>();

  const fullLocation = computed(() => {
    return props.location ? `гид ${props.location}` : '';
  });

  const guideProfilePath = computed(() => `/guide/${props.id}`);

  const formattedExperienceCount = computed(
    () =>
      `${props.experienceCount} ${pluralize(props.experienceCount, ['экскурсия', 'экскурсии', 'экскурсий'])} на Трипстере`,
  );

  const handleGuideClick = () => emit('guide-clicked', props.id);
</script>

<style lang="scss" scoped>
  .guide-quotes-quote {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    color: inherit;
    text-decoration: none;

    &:hover,
    &:active {
      text-decoration: none;
    }
  }

  .guide-quotes-quote__title,
  .guide-quotes-quote__text {
    :deep(> *:first-child) {
      margin-top: 0;
      padding-top: 0;
    }
  }

  .guide-quotes-quote__title,
  .guide-quotes-quote__title :deep(p) {
    @include base-500-400-extra;

    @include tablet {
      @include base-400-400-extra;
    }
  }

  .guide-quotes-quote__text {
    @include base-500-500-regular;

    @include tablet {
      @include base-400-400-regular;
    }
  }

  .guide-quotes-quote__info {
    display: flex;
    gap: var(--alias-space-12);
    padding-top: var(--alias-space-8);
  }

  .guide-quotes-quote__info-text {
    display: flex;
    flex-direction: column;
    justify-content: center;

    @include base-500-400-regular;

    @include tablet {
      @include base-400-300-regular;
    }
  }

  .guide-quotes-quote__avatar {
    flex-grow: 0;
    flex-shrink: 0;
    object-fit: cover;
    width: 48px;
    height: 48px;
    border-radius: var(--alias-radius-rounded);
    background-color: var(--alias-color-background-default);

    @include tablet {
      width: 32px;
      height: 32px;
    }
  }

  .guide-quotes-quote__guide-name {
    color: var(--alias-color-link-default);
    text-decoration: none;

    @include base-500-400-regular;

    @include tablet {
      @include base-400-300-regular;
    }
  }

  .guide-quotes-quote:hover .guide-quotes-quote__guide-name {
    color: var(--alias-color-link-default-hovered);
  }

  .guide-quotes-quote__experiences {
    color: var(--alias-color-text-subtle);

    @include base-400-300-regular;

    @include tablet {
      @include base-300-300-regular;
    }
  }
</style>
